/**
 * W67: Compare's Overlay, the classic drawing overlay (Bluebeam / Procore / Autodesk Build): each revision's page as a
 * luminance mask, ink only in Rev A red (removed), ink only in Rev B blue (added), ink in both dark grey, paper white.
 * Pure helpers, so the colouring and the alignment are tested on tiny fixtures.
 */

/** A pixel counts as ink this dark or darker (0 white … 1 black), alpha-weighted (transparent is paper) */
export const INK_DARKNESS = 0.35;
/** Above this many pixels the colouring runs in a Worker (a 16 MP sharp render would hold the page for > 100 ms) */
export const WORKER_PIXELS = 2_000_000;

export interface OverlayOptions {
  showRemoved: boolean;
  showAdded: boolean;
  /** Rev A ↔ Rev B, 0 … 1: at 0 only Rev A's own ink shows, at 1 only Rev B's; both full in the middle */
  balance: number;
}

/** What each pixel is: 0 paper, 1 ink only in Rev A, 2 only in Rev B, 3 in both */
export type PixelClasses = Uint8Array;

/**
 * Review I1: the masks are read once into one class per pixel; the slider and the toggles then only repaint from the
 * classes (a 4-entry palette), which is cheap. Both functions are self-contained: the Worker gets their source text.
 */
export function classifyPixels(a: Uint8ClampedArray, b: Uint8ClampedArray): Uint8Array {
  const ink = 0.35;
  const classes = new Uint8Array(a.length / 4);
  for (let i = 0, p = 0; i < a.length; i += 4, p++) {
    const da = (1 - (0.299 * a[i] + 0.587 * a[i + 1] + 0.114 * a[i + 2]) / 255) * (a[i + 3] / 255);
    const db = (1 - (0.299 * b[i] + 0.587 * b[i + 1] + 0.114 * b[i + 2]) / 255) * (b[i + 3] / 255);
    classes[p] = (da >= ink ? 1 : 0) + (db >= ink ? 2 : 0);
  }
  return classes;
}

export function paintClasses(classes: Uint8Array, out: Uint8ClampedArray, showRemoved: boolean, showAdded: boolean, balance: number): void {
  // #E02020 removed, #1036D6 added, #060B1E at 70% in both, on white
  const wa = showRemoved ? Math.min(1, 2 * (1 - balance)) : 0;
  const wb = showAdded ? Math.min(1, 2 * balance) : 0;
  const mix = (c: number, k: number) => Math.round(255 + (c - 255) * k);
  const palette = [
    [255, 255, 255],
    [mix(0xe0, wa), mix(0x20, wa), mix(0x20, wa)],
    [mix(0x10, wb), mix(0x36, wb), mix(0xd6, wb)],
    [mix(0x06, 0.7), mix(0x0b, 0.7), mix(0x1e, 0.7)],
  ];
  for (let p = 0, i = 0; p < classes.length; p++, i += 4) {
    const c = palette[classes[p]];
    out[i] = c[0];
    out[i + 1] = c[1];
    out[i + 2] = c[2];
    out[i + 3] = 255;
  }
}

/** Colours `out` (RGBA) from two same-size RGBA pages in one go (the classes, then the palette) */
export function overlayPixels(a: Uint8ClampedArray, b: Uint8ClampedArray, out: Uint8ClampedArray, showRemoved: boolean, showAdded: boolean, balance: number): void {
  paintClasses(classifyPixels(a, b), out, showRemoved, showAdded, balance);
}

/** Something that runs a script off the main thread (a Worker; specs pass a stand-in) */
export interface OverlayWorker {
  onmessage: ((e: MessageEvent) => void) | null;
  onerror: ((e: unknown) => void) | null;
  postMessage(message: unknown, transfer?: Transferable[]): void;
  terminate(): void;
  /** Frees the script's Blob URL (kept until the Worker goes, so its load never races the revoke) */
  revokeUrl?: () => void;
}

const workerSource = () => `const classifyPixels = ${classifyPixels.toString()};
const paintClasses = ${paintClasses.toString()};
let classes = null;
self.onmessage = e => {
  const d = e.data;
  if (d.type === 'masks') {
    classes = classifyPixels(new Uint8ClampedArray(d.a), new Uint8ClampedArray(d.b));
    return;
  }
  const out = new Uint8ClampedArray(classes.length * 4);
  paintClasses(classes, out, d.removed, d.added, d.balance);
  self.postMessage({ id: d.id, out }, [out.buffer]);
};`;

/**
 * Review I1: one colourer per compare session. A big page's masks go to ONE long-lived Worker once (transferred, not
 * copied); each slider or toggle change then sends only its options. One colouring runs at a time and only the latest
 * request waits, so dragging the slider never piles up work. Small pages stay on the main thread.
 */
export class OverlayColourer {
  /** Pages this big or bigger go to the Worker (specs lower it) */
  static workerPixels = WORKER_PIXELS;
  /** Specs replace this to watch the Worker */
  static createWorker: () => OverlayWorker | null = () => {
    if (typeof Worker === 'undefined' || typeof Blob === 'undefined' || typeof URL?.createObjectURL !== 'function') {
      return null;
    }
    const url = URL.createObjectURL(new Blob([workerSource()], { type: 'text/javascript' }));
    try {
      return Object.assign(new Worker(url), { revokeUrl: () => URL.revokeObjectURL(url) }) as unknown as OverlayWorker;
    } catch (error) {
      URL.revokeObjectURL(url);
      throw error;
    }
  };

  private worker: OverlayWorker | null = null;
  /** Review N1: the Worker failed: the main thread colours for the rest of the session */
  private workerFailed = false;
  /** Called after a Worker failure: the owner sends the masks again (the Worker had the only copy) */
  onWorkerLost: (() => void) | null = null;
  private classes: Uint8Array | null = null;
  private size: { width: number; height: number } | null = null;
  private busy = false;
  private nextId = 0;
  private pending: { options: OverlayOptions; resolve: (out: ImageData | null) => void } | null = null;
  private current: { id: number; resolve: (out: ImageData | null) => void } | null = null;

  /** New page masks (a new render): they replace the old ones; a colouring waiting on the old ones resolves null */
  setMasks(a: ImageData, b: ImageData): void {
    this.size = { width: a.width, height: a.height };
    this.pending?.resolve(null);
    this.pending = null;
    if (this.workerFailed || a.width * a.height < OverlayColourer.workerPixels) {
      this.classes = classifyPixels(a.data, b.data);
      return;
    }
    this.classes = null;
    if (!this.worker) {
      try {
        this.worker = OverlayColourer.createWorker();
      } catch {
        this.worker = null;
      }
      if (this.worker) {
        this.worker.onmessage = e => this.received(e.data as { id: number; out: Uint8ClampedArray });
        this.worker.onerror = () => this.workerLost();
      }
    }
    if (!this.worker) {
      this.classes = classifyPixels(a.data, b.data);
      return;
    }
    // Moved, not copied: the Worker keeps the classes, the main thread keeps nothing
    this.worker.postMessage({ type: 'masks', a: a.data.buffer, b: b.data.buffer }, [a.data.buffer, b.data.buffer]);
  }

  /** The colours for these options, or null when a newer request (or new masks) took its place */
  colour(options: OverlayOptions): Promise<ImageData | null> {
    if (!this.size) {
      return Promise.resolve(null);
    }
    if (this.classes) {
      const out = new ImageData(this.size.width, this.size.height);
      paintClasses(this.classes, out.data, options.showRemoved, options.showAdded, options.balance);
      return Promise.resolve(out);
    }
    return new Promise(resolve => {
      this.pending?.resolve(null);
      this.pending = { options, resolve };
      this.pump();
    });
  }

  terminate(): void {
    this.dropWorker();
    this.classes = null;
    this.size = null;
  }

  private pump(): void {
    if (this.busy || !this.pending || !this.worker) {
      return;
    }
    const { options, resolve } = this.pending;
    this.pending = null;
    this.busy = true;
    const id = ++this.nextId;
    this.current = { id, resolve };
    this.worker.postMessage({ type: 'colour', id, removed: options.showRemoved, added: options.showAdded, balance: options.balance });
  }

  private received(data: { id: number; out: Uint8ClampedArray }): void {
    this.busy = false;
    const current = this.current;
    this.current = null;
    if (current && current.id === data.id && this.size) {
      // A newer request is waiting: this answer is already old
      current.resolve(this.pending ? null : new ImageData(new Uint8ClampedArray(data.out.buffer as ArrayBuffer), this.size.width, this.size.height));
    }
    this.pump();
  }

  /** Review N1: every waiting colouring settles (null: repaint after the masks come back), and the owner re-sends them */
  private workerLost(): void {
    this.workerFailed = true;
    this.dropWorker();
    this.onWorkerLost?.();
  }

  private dropWorker(): void {
    this.worker?.terminate();
    this.worker?.revokeUrl?.();
    this.worker = null;
    this.busy = false;
    this.current?.resolve(null);
    this.current = null;
    this.pending?.resolve(null);
    this.pending = null;
  }
}

export interface PagePoint {
  x: number;
  y: number;
}

/** p ↦ (a·x − b·y + tx, b·x + a·y + ty): uniform scale √(a² + b²), rotation atan2(b, a), then translate */
export interface Similarity {
  a: number;
  b: number;
  tx: number;
  ty: number;
}

export const IDENTITY: Similarity = { a: 1, b: 0, tx: 0, ty: 0 };

/**
 * Align: the similarity that takes Rev B's two points onto the same two points on Rev A (complex numbers:
 * m = (a2 − a1) / (b2 − b1), t = a1 − m·b1). Null when B's points coincide.
 */
export function similarityFromPairs(a1: PagePoint, a2: PagePoint, b1: PagePoint, b2: PagePoint): Similarity | null {
  const vbx = b2.x - b1.x;
  const vby = b2.y - b1.y;
  const den = vbx * vbx + vby * vby;
  if (den < 1e-12) {
    return null;
  }
  const vax = a2.x - a1.x;
  const vay = a2.y - a1.y;
  const a = (vax * vbx + vay * vby) / den;
  const b = (vay * vbx - vax * vby) / den;
  return { a, b, tx: a1.x - (a * b1.x - b * b1.y), ty: a1.y - (b * b1.x + a * b1.y) };
}

export function applySimilarity(t: Similarity, p: PagePoint): PagePoint {
  return { x: t.a * p.x - t.b * p.y + t.tx, y: t.b * p.x + t.a * p.y + t.ty };
}

/** The other way: a point on the drawn (aligned) Rev B back to Rev B's own page */
export function invertSimilarity(t: Similarity): Similarity {
  const den = t.a * t.a + t.b * t.b;
  const a = t.a / den;
  const b = -t.b / den;
  return { a, b, tx: -(a * t.tx - b * t.ty), ty: -(b * t.tx + a * t.ty) };
}

/** A compared revision's name: "Rev B", or "Draft" for a set review page not yet published (it has no label yet) */
export function revisionName(rev: { status?: string; label?: string | null } | null | undefined): string {
  return rev?.status === 'draft' ? 'Draft' : `Rev ${rev?.label ?? ''}`.trim();
}
